import React, {
    useRef,
    useEffect,
    useState,
    useContext,
    FormEvent,
} from "react";
import {
    Container,
    Form,
    Button,
    Card,
    Navbar,
    Row,
    Col,
} from "react-bootstrap";
import { UserDataContext } from "store/UserContext";
import { ReactComponent as Logo } from "assets/images/kitchen-logo.svg";
import { ReactComponent as LoginLock } from "assets/images/login-lock.svg";
import { ReactComponent as Visibility } from "assets/icons/visibility.svg";
import { ReactComponent as NoVisibility } from "assets/icons/no-visibility.svg";
import { persianToEnglishNumber, validator } from "utils/helpers";
import { login, sendOtp } from "Services/Login";
import { useMutation } from "@tanstack/react-query";
import LocalStorage from "utils/LocalStorage";
import "./style.scss";
import useTimer from "../../hooks/useTimer";
import { LoginResponseType } from "../../Services/Login/LoginService.props";

const Login = () => {
    const phoneRef = useRef<HTMLInputElement | null>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const OtpRef = useRef<HTMLInputElement>(null);
    const TotpRef = useRef<HTMLInputElement>(null);
    const [, setToken] = useContext(UserDataContext);

    const [errors, setErrors] = useState<{ [key: string]: string | null }>({});
    const [showPassword, setShowPassword] = useState(false);
    const [otpVisible, setOtpVisible] = useState(false); // Initially hide OTP input and timer
    const [totpVisible, setTotpVisible] = useState(false); // Initially hide TOTP input and timer
    const [timeParse, finish, setNewTime] = useTimer(0);

    const { mutateAsync: mutateSendOtp } = useMutation(sendOtp);
    const {
        data: responseLogin,
        mutateAsync: mutateLogin,
        isError,
        error,
    } = useMutation(login);

    useEffect(() => {
        if (responseLogin?.data && !isError) {
            LocalStorage.setToken(responseLogin.data);
            setToken(responseLogin.data?.access_token);
        }
    }, [responseLogin?.data, isError, setToken]);

    const validateFields = () => {
        const phone = phoneRef?.current?.value;
        const password = passwordRef?.current?.value;
        const newErrors: { [key: string]: string } = {};

        if (!validator.mobileNumberHasZero(phone)) {
            newErrors.phone = ".شماره تلفن همراه به‌درستی وارد نشده‌است";
        }
        if (!password) {
            newErrors.password = ".لطفا پسورد خود را وارد کنید";
        }
        return newErrors;
    };

    const clearErrors = (fields: string[]) => {
        const clearedErrors = { ...errors };
        fields.forEach((field) => {
            if (errors[field]) clearedErrors[field] = null;
        });
        setErrors(clearedErrors);
    };

    const handleSendOtp = async (e: FormEvent) => {
        e.preventDefault();

        const newErrors = validateFields();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            clearErrors(["phone", "password"]);

            const phone = phoneRef?.current?.value;
            const password = passwordRef?.current?.value;
            if (!phone || !password) return;

            const data = { phone: persianToEnglishNumber(phone), password: persianToEnglishNumber(password) };
            mutateLogin(data).then(() => {
                mutateSendOtp({ phone }).then(() => {
                    setNewTime(120);
                    setOtpVisible(true);
                    setTotpVisible(false);
                });
            });
        }
    };

    const handleSendTotp = async (e: FormEvent) => {
        e.preventDefault();

        const newErrors = validateFields();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            clearErrors(["phone", "password"]);

            const phone = phoneRef?.current?.value;
            const password = passwordRef?.current?.value;
            if (!phone || !password) return;

            const data = { phone: persianToEnglishNumber(phone), password: persianToEnglishNumber(password) };
            mutateLogin(data).then(() => {
                setTotpVisible(true);
                setOtpVisible(false);
            });
        }
    };

    const handleLogin = async () => {
        const newErrors = validateFields();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            clearErrors(["phone", "password", "otp"]);

            const phone = phoneRef?.current?.value;
            const password = passwordRef?.current?.value;
            const otp = OtpRef?.current?.value;
            const totp = TotpRef?.current?.value;
            if (!phone || !password) return;
            if (!totp && !otp) return;

            const data = { phone: persianToEnglishNumber(phone), password: persianToEnglishNumber(password) };
            if (otpVisible) {
                // @ts-ignore
                data.otp = persianToEnglishNumber(otp);
            } else {
                // @ts-ignore
                data.otp = persianToEnglishNumber(totp);
            }
            mutateLogin(data).then((response) => {
                LocalStorage.setToken(response.data as LoginResponseType);
                setToken(response.data?.access_token);
            });
        }
    };

    return (
        <div className="login-wrapper" data-testid="Login">
            <Navbar className="rtl bg-white">
                <Container>
                    <Navbar.Brand>
                        <Logo />
                    </Navbar.Brand>
                </Container>
            </Navbar>
            <Container className="mt-4">
                <Row className="d-flex flex-row justify-content-center">
                    <Col
                        md={5}
                        className="lock-image d-flex justify-content-end align-items-end"
                    >
                        <LoginLock />
                    </Col>
                    <Col md={5}>
                        <Card
                            className="mx-auto border-0 bg-white"
                            style={{ borderRadius: "18px" }}
                        >
                            <Card.Body>
                                <>
                                    <Card.Title className="pb-1">
                                        ورود به پنل اسنپ کیچن
                                    </Card.Title>
                                    {error && (
                                        <Card.Text className="text-danger">
                                            {(error as any)?.response
                                                ?.status !== 404
                                                ? ".اطلاعات وارد‌شده نادرست است"
                                                : "خطا در سرور"}
                                        </Card.Text>
                                    )}
                                    <Form className="p-2">
                                        <Form.Group
                                            className="mb-3"
                                            controlId="phone"
                                        >
                                            <Form.Label>
                                                شماره تلفن همراه
                                            </Form.Label>
                                            <Form.Control
                                                type="tel"
                                                className="hasBorder"
                                                name="phone"
                                                autoComplete="on"
                                                ref={phoneRef}
                                                autoFocus
                                                isInvalid={!!errors.phone}
                                                disabled={
                                                    otpVisible || totpVisible
                                                }
                                            />
                                            <Form.Text>
                                                .شماره با ۰۹ شروع ‌می‌شود
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.phone}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group
                                            className="mb-3 position-relative"
                                            controlId="password"
                                        >
                                            <Form.Label>رمز عبور</Form.Label>
                                            <Form.Control
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                className="hasBorder"
                                                name="password"
                                                autoComplete="on"
                                                ref={passwordRef}
                                                isInvalid={!!errors.password}
                                                disabled={
                                                    otpVisible || totpVisible
                                                }
                                            />
                                            {showPassword ? (
                                                <NoVisibility
                                                    data-testid="Login__hide"
                                                    className="visibility-icon"
                                                    onClick={() =>
                                                        setShowPassword(false)
                                                    }
                                                />
                                            ) : (
                                                <Visibility
                                                    data-testid="Login__visible"
                                                    className="visibility-icon"
                                                    onClick={() =>
                                                        setShowPassword(true)
                                                    }
                                                />
                                            )}
                                            <Form.Control.Feedback type="invalid">
                                                {errors.password}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {otpVisible && (
                                            <Form.Group
                                                className="mb-3 position-relative mt-4"
                                                controlId="otp"
                                            >
                                                <div className="d-flex align-content-center justify-content-between">
                                                    <div>
                                                        {finish ? (
                                                            <div
                                                                style={{
                                                                    cursor: "pointer",
                                                                }}
                                                                onClick={
                                                                    handleSendOtp
                                                                }
                                                            >
                                                                ارسال مجدد رمز
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                {timeParse}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <Form.Label>
                                                        تایید دو‌ مرحله‌ای
                                                    </Form.Label>
                                                </div>
                                                <Form.Control
                                                    type="tel"
                                                    className="hasBorder"
                                                    name="otp"
                                                    ref={OtpRef}
                                                    isInvalid={!!errors.otp}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.otp}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        )}
                                        {totpVisible && (
                                            <Form.Group
                                                className="mb-3 position-relative mt-4"
                                                controlId="totp"
                                            >
                                                <Form.Label>
                                                    تایید دو‌ مرحله‌ای
                                                </Form.Label>
                                                <Form.Control
                                                    type="tel"
                                                    className="hasBorder"
                                                    name="totp"
                                                    ref={TotpRef}
                                                    isInvalid={!!errors.totp}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.totp}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        )}
                                        {(otpVisible || totpVisible) && (
                                            <Button
                                                variant={
                                                    otpVisible
                                                        ? "light"
                                                        : "secondary"
                                                }
                                                className="w-100 border-0 pt-2 pb-2 mt-4"
                                                dir="rtl"
                                                onClick={handleLogin}
                                                style={{
                                                    backgroundColor: "#FF00A6",
                                                }}
                                            >
                                                ورود
                                            </Button>
                                        )}
                                        {!totpVisible && (
                                            <Button
                                                variant={
                                                    otpVisible
                                                        ? "light"
                                                        : "secondary"
                                                }
                                                className="w-100 border-0 pt-2 pb-2 mt-4"
                                                onClick={handleSendTotp}
                                                dir="rtl"
                                                style={{
                                                    backgroundColor: otpVisible
                                                        ? "unset"
                                                        : "#FF00A6",
                                                }}
                                            >
                                                ورود با رمز دوم یکبار مصرف از
                                                Authenticator
                                            </Button>
                                        )}
                                        {!otpVisible && (
                                            <Button
                                                onClick={handleSendOtp}
                                                variant="light"
                                                className="w-100 border-0 pt-2 pb-2 mt-4"
                                                dir="rtl"
                                            >
                                                ورود با رمز دوم یکبار مصرف از
                                                طریق پیامک
                                            </Button>
                                        )}
                                    </Form>
                                </>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <div className="d-flex align-items-end position-relative flex-grow-1">
                <Card
                    className="w-100 mt-3 text-white rounded-0"
                    style={{ backgroundColor: "#0E2654" }}
                >
                    <Card.Body className="font-weight-light container">
                        <Card.Text>.حق کپی برای اسنپ! کیچن محفوظ است</Card.Text>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default Login;
